import type { HomeTableType, CenterHours } from '#shared/types/appTypes/rhPresencesTable/homeTable'
import type { RhItemType } from '#shared/types/dbTypes/productionTypes'
import type { RhPresenceType } from '#shared/types/dbTypes/rhpresencesTypes'
import type { AppHollydayType } from '#shared/types/appTypes/responseDbTypes/homeTypes'
import { formatDayToString, isSunday } from '~/utils/dateUtils'

export type PayloadHomeTableType = {
  'homeTableArray': HomeTableType[]
}

function returnErrorData(): PayloadHomeTableType {
  return {
    'homeTableArray': []
  }
}

function getInitalData(rhArray: RhItemType[]) {
  const uniqueRhs: HomeTableType[] = []

  for (let i = 0; i < rhArray.length; i++) {
    const rhItem = rhArray[i]
    if (!rhItem) continue
    uniqueRhs.push({
      'days': [],
      'globalTotalExtraHours': 0,
      'globalTotalHours': 0,
      'globalTotalUsualHours': 0,
      'isUserColaborator': false,
      'rhId': rhItem.rhId,
      'rhName': rhItem.name,
      'rhProfession': rhItem.profession,
      'rhStatus': rhItem.status ?? { 'isActive': true, 'inactiveDate': null }
    })
  }

  return uniqueRhs
}

function checkIsUserCollaborator(dbUserColaborators: string[], itemRhid: string) {
  const findedRhInUser = dbUserColaborators.find(item => item == itemRhid)
  if (!findedRhInUser) return false
  return true
}

// Using isSunday from dateUtils instead of local function

function generateWorkPeriodDates(startDate: string, endDate: string): string[] {
  const dates: string[] = []
  const start = new Date(startDate)
  const end = new Date(endDate)

  for (let current = new Date(start); current <= end; current.setDate(current.getDate() + 1)) {
    const year = current.getFullYear()
    const month = (current.getMonth() + 1).toString().padStart(2, '0')
    const day = current.getDate().toString().padStart(2, '0')
    dates.push(`${year}-${month}-${day}`)
  }

  return dates
}

function getLastDay(dbPresences: RhPresenceType[]): number | null {
  if (!dbPresences) return null
  if (dbPresences.length == 0) return null

  const sortedData = dbPresences.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  )
  const lastDay = sortedData[0]
  if (lastDay) return Number(lastDay.date.slice(8, 10))
  return null
}

function getHoldidayClass(findedHolidayInThisDay: string | undefined, isSundayDay: boolean) {
  if (findedHolidayInThisDay) return 'holiday'
  if (isSundayDay) return 'sunday'
  return 'regular'
}

export function getCompletePresencesTable(
  dbPresences: RhPresenceType[],
  dbUserColaborators: string[],
  lastDayOfWorkPeriod: string | null,
  firstDayOfTheMonth: string | null,
  holidays: AppHollydayType[],
  rhArray: RhItemType[]
): PayloadHomeTableType {
  const dbLastDay = getLastDay(dbPresences)

  if (!dbLastDay) return returnErrorData()

  if (!dbPresences || !rhArray) return returnErrorData()
  if (dbPresences.length == 0 || rhArray.length == 0) return returnErrorData()
  if (!firstDayOfTheMonth || !lastDayOfWorkPeriod) return returnErrorData()

  /*
    @description: the name uniqueRh is a bit confusin, but it means it will be the array of the uniques Rh in the database for that month.
  */
  const uniqueRhs: HomeTableType[] = getInitalData(rhArray)

  const firstDayMonthStr = firstDayOfTheMonth?.slice(5, 7)
  const lastDayMonthStr = lastDayOfWorkPeriod?.slice(5, 7)

  const findedHoliday = holidays.map(el => `${el.month}-${el.day}`)

  // Generate dates for the work period
  const workPeriodDates: string[] = generateWorkPeriodDates(firstDayOfTheMonth, lastDayOfWorkPeriod)

  for (let j = 0; j < uniqueRhs.length; j++) {
    const uniqueRh = uniqueRhs[j]
    if (!uniqueRh) continue

    uniqueRh.isUserColaborator = checkIsUserCollaborator(dbUserColaborators, uniqueRh.rhId)

    for (let k = 0; k < workPeriodDates.length; k++) {
      const referenceDate = workPeriodDates[k]
      if (!referenceDate) continue

      const day = referenceDate.slice(8, 10)
      const month = referenceDate.slice(5, 7)
      const refDateStr = `${month}-${day}`
      const rhItemInThisDay = dbPresences.find(
        item => item.date == referenceDate && item.rhId == uniqueRh.rhId
      )

      const isSundayDay = isSunday(referenceDate)
      const findedHolidayInThisDay = findedHoliday.find(el => el === refDateStr)

      if (rhItemInThisDay) {
        let totalUsualHours = 0
        let totalExtraHours = 0
        let totalHours = 0
        const faults = rhItemInThisDay.fault

        const centersHours: CenterHours[] = []

        if (rhItemInThisDay.hours.length > 0) {
          for (let l = 0; l < rhItemInThisDay.hours.length; l++) {
            const dbItemInThisDay = rhItemInThisDay.hours[l]
            if (!dbItemInThisDay) continue

            let centerUsualHours = 0
            let centerExtraHours = 0

            for (let m = 0; m < dbItemInThisDay.activities.length; m++) {
              const activity = dbItemInThisDay.activities[m]
              if (!activity) continue
              centerUsualHours += activity.usualHours
              centerExtraHours += activity.extraHours
            }

            centersHours.push({
              'code': dbItemInThisDay.center,
              'extraHours': centerExtraHours,
              'usualHours': centerUsualHours
            })

            totalUsualHours += centerUsualHours
            totalExtraHours += centerExtraHours
            totalHours += centerUsualHours + centerExtraHours
          }
        }

        uniqueRh.days.push({
          '_id': typeof rhItemInThisDay._id === 'string' ? rhItemInThisDay._id : undefined,
          centersHours,
          'cssClass': getHoldidayClass(findedHolidayInThisDay, isSundayDay),
          'day': day,
          'date': referenceDate,
          'faults': faults,
          'usualHours': totalUsualHours,
          'extraHours': totalExtraHours,
          'totalHours': totalHours
        })

        uniqueRh.globalTotalUsualHours += totalUsualHours
        uniqueRh.globalTotalExtraHours += totalExtraHours
        uniqueRh.globalTotalHours += totalHours
      } else if (!rhItemInThisDay && !isSundayDay && !findedHolidayInThisDay) {
        uniqueRh.days.push({
          'cssClass': 'error',
          'centersHours': [],
          'day': day,
          'date': referenceDate,
          'extraHours': 0,
          'isHoliday': findedHolidayInThisDay ? true : false,
          'totalHours': 0,
          'usualHours': 0
        })
      } else if (!rhItemInThisDay && (isSundayDay || findedHolidayInThisDay)) {
        // TODO: Add day with appropriate CSS class (sunday or holiday)
        uniqueRh.days.push({
          'centersHours': [],
          'cssClass': isSundayDay ? 'sunday' : 'holiday',
          'day': day,
          'date': referenceDate,
          'usualHours': 0,
          'extraHours': 0,
          'totalHours': 0
        })
      }
    }
  }
  return { 'homeTableArray': uniqueRhs }
}
